import { Fragment, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, Users, Building2, ClipboardList, ScrollText,
    LogOut, GraduationCap, History, CalendarClock, Receipt, Coins,
    Menu, X, Route as RouteIcon, Stethoscope, ClipboardCheck, HeartHandshake,
    ChevronLeft, ChevronRight, ShieldCheck, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ROLE_LABEL } from "@/lib/roles";
import { daftarKelompok } from "@/lib/menu";
import IdleTimeoutManager from "@/components/IdleTimeoutManager";
import ThemeToggle from "@/components/ThemeToggle";
import logo from "../assets/logo-aura.png";
import { TAGLINE } from "../lib/brand";

const IKON_MENU = {
    dashboard: LayoutDashboard, peserta: GraduationCap, riwayat: History, rotasi: RouteIcon,
    orientasi: ClipboardCheck, peer: HeartHandshake, unitCost: Receipt, prodi: Building2, users: Users,
    periode: CalendarClock, tarif: Coins, keamanan: ShieldCheck, dataRotasi: Stethoscope, audit: ScrollText,
    keaktifan: Activity, survei: ClipboardList,
};

// Preferensi collapse sidebar disimpan PER PERANGKAT (localStorage), sama seperti
// pola simonev_theme_mode di ThemeContext.jsx -- supaya tidak reset tiap refresh/pindah halaman.
const SIDEBAR_COLLAPSE_KEY = "simonev_sidebar_collapsed";

function readStoredCollapsed() {
    try { return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1"; } catch { return false; }
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const role = user?.role;
    const [mobileOpen, setMobileOpen] = useState(false);
    const closeMobile = () => setMobileOpen(false);
    // Collapse HANYA berlaku di desktop (md:) -- drawer mobile selalu full width/label,
    // tidak ada tombol collapse terpisah di sana (lihat header <aside> & kelas md:* di bawah).
    const [collapsed, setCollapsed] = useState(readStoredCollapsed);
    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? "1" : "0"); } catch {}
            return next;
        });
    };

    // Isi menu per peran ada di lib/menu.js (diuji); di sini hanya memetakan ikonnya. Menu dikelompokkan; judul kelompok
    // selalu tampil (tidak bisa dilipat), kecuali kelompok pertama (Dashboard) yg tanpa judul.
    const kelompok = daftarKelompok(role).map((k) => ({ ...k, menu: k.menu.map((it) => ({ ...it, icon: IKON_MENU[it.icon] })) }));

    return (
        // h-screen + overflow-hidden: shell dikunci pas tinggi viewport dan TIDAK PERNAH
        // scroll sendiri (dulu ini "min-h-screen" tanpa overflow control, jadi begitu
        // konten <main> lebih tinggi dari layar, seluruh DOKUMEN yg scroll -> sidebar
        // (waktu itu "position: static" di desktop) ikut ke-scroll keluar layar. Sekarang
        // hanya kolom konten (di bawah) yg overflow-y-auto; sidebar tidak pernah bergerak.
        <div className="h-screen flex bg-slate-50 overflow-hidden">
            <IdleTimeoutManager />

            {/* Backdrop drawer mobile: klik utk tutup sidebar */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 md:hidden"
                    onClick={closeMobile}
                    aria-hidden="true"
                    data-testid="sidebar-backdrop"
                />
            )}

            <aside
                className={`fixed inset-y-0 left-0 z-40 w-64 flex-shrink-0 bg-[#267B84] text-white flex flex-col
                    transform transition-all duration-300 ease-in-out
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                    md:sticky md:top-0 md:h-screen md:inset-auto md:translate-x-0
                    ${collapsed ? "md:w-14" : "md:w-56"}`}
            >
                <div className={`px-5 py-6 border-b border-white/10 flex items-center justify-between ${collapsed ? "md:justify-center md:px-2" : ""}`}>
                    {/* Wordmark putih "AURA" (logo-aura.png, dipotong dari logo lama tanpa tagline) +
                        tagline sbg TEKS (lib/brand.js) -- dipasang langsung di atas sidebar teal
                        (#267B84) tanpa bingkai. Lebar blok tetap (w-32 / md:w-36) = lebar logo, tagline
                        dibungkus ke baris berikutnya bila perlu, jadi tidak pernah menabrak tombol.
                        Disembunyikan saat collapsed -- rail collapsed cukup diwakili tombol toggle. */}
                    {/* Lebih kecil di mobile (drawer sempit, harus berbagi baris dgn tombol close) */}
                    <div className={`w-32 md:w-36 shrink-0 ${collapsed ? "md:hidden" : ""}`} data-testid="sidebar-brand">
                        <img src={logo} alt="AURA PPDS" className="w-full h-auto" />
                        <p className="mt-1.5 text-[10px] md:text-[11px] leading-snug text-white/85" data-testid="sidebar-tagline">{TAGLINE}</p>
                    </div>
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? "Perluas menu" : "Ciutkan menu"}
                        aria-pressed={collapsed}
                        data-testid="sidebar-collapse-toggle"
                        className="hidden md:inline-flex shrink-0 p-2 rounded-md text-white/70 hover:text-white hover:bg-black/10 transition-colors"
                    >
                        {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                    </button>
                    <button
                        type="button"
                        onClick={closeMobile}
                        aria-label="Tutup menu"
                        data-testid="sidebar-close-button"
                        className="md:hidden shrink-0 p-1.5 -mr-1.5 rounded-md text-white/70 hover:text-white hover:bg-black/10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <TooltipProvider delayDuration={200}>
                    {/* space-y lebih renggang saat collapsed (md:space-y-2) -- rail ikon-saja
                        butuh jarak antar item lebih lega drpd versi berlabel, krn tidak ada
                        teks yg secara visual "mengisi" ruang di antara ikon. */}
                    <nav className={`flex-1 min-h-0 px-3 py-4 overflow-y-auto ${collapsed ? "md:px-2" : ""}`} data-testid="sidebar-nav" aria-label="Menu utama">
                        {kelompok.map((k, gi) => (
                        <div key={k.id} data-testid={`menu-kelompok-${k.id}`} className={`space-y-1 ${collapsed ? "md:space-y-2" : ""} ${gi > 0 ? "mt-4" : ""}`}>
                        {/* Judul kelompok: teks #EAF7F8 (4,5:1 di atas teal #267B84; putih redup < 4,5 gagal AA); saat rail
                            diciutkan diganti garis pemisah tipis, di HP/laci tampil seperti mode lebar. */}
                        {k.judul && (
                            <>
                                <div className={`px-4 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#EAF7F8] ${collapsed ? "md:hidden" : ""}`}
                                     data-testid={`menu-judul-${k.id}`}>
                                    {k.judul}
                                </div>
                                {collapsed && <div className="hidden border-t border-white/25 md:block" role="separator" data-testid={`menu-garis-${k.id}`} />}
                            </>
                        )}
                        {k.menu.map((it) => {
                            const link = (
                                <NavLink
                                    to={it.to}
                                    end={it.end}
                                    data-testid={it.testId}
                                    onClick={closeMobile}
                                    // px-4 py-2.5 dipindah ke sini (bukan di kelas CSS .sidebar-link) supaya
                                    // varian md:px-2/md:py-3 di bawah DIJAMIN menang saat collapsed --
                                    // lihat catatan di .sidebar-link (index.css). CATATAN REGRESI: dulu
                                    // "Riwayat Import" (it.indent) diberi pl-9 dgn asumsi kalah lawan
                                    // px-4 milik .sidebar-link (bare CSS, sumber file-order-nya sama spt
                                    // masalah py-2.5 di atas) -- makanya sebelum ini TIDAK PERNAH benar2
                                    // menjorok. Setelah px-4 pindah jadi utility biasa di sini, pl-9 (kelas
                                    // single-side, urutan generate Tailwind-nya SELALU setelah px-*) jadi
                                    // benar2 menang & item itu tiba2 menjorok (dilaporkan 29 Sep 2026) --
                                    // pl-9 sengaja DIHAPUS di sini spy semua item sejajar rata kiri.
                                    className={({ isActive }) => `sidebar-link px-4 py-2.5 ${isActive ? "active" : ""} ${it.indent ? "text-[13px]" : ""} ${collapsed ? "md:justify-center md:px-2 md:py-3" : ""} ${collapsed && isActive ? "md:pl-1.5" : ""}`}
                                >
                                    {/* Ikon dibesarkan ke 20px (w-5 h-5) saat collapsed -- skala yg sama
                                        dgn tombol toggle/close/hamburger di file ini, supaya proporsinya
                                        konsisten, bukan cuma menu utama yg membesar. */}
                                    <it.icon className={`${it.indent ? "w-3.5 h-3.5" : "w-4 h-4"} ${collapsed ? "md:w-5 md:h-5" : ""}`} />
                                    <span className={collapsed ? "md:hidden" : ""}>{it.label}</span>
                                </NavLink>
                            );
                            // Tooltip hanya berguna saat rail diciutkan (label ikon disembunyikan);
                            // di mobile (drawer selalu full label) ini praktis tidak pernah muncul.
                            return collapsed ? (
                                <Tooltip key={it.to}>
                                    {/* <span> pembungkus: Slot (asChild) menggabungkan className, dan className
                                        berbentuk fungsi milik NavLink ikut ditulis sbg teks sehingga kelas
                                        .sidebar-link hilang (menu jadi inline, rata kiri). Dgn span, NavLink
                                        tetap memegang className-nya sendiri. md:pl-1.5 saat aktif =
                                        mengganti 2px border-l penanda aktif supaya ikon tetap di tengah. */}
                                    <TooltipTrigger asChild><span className="block">{link}</span></TooltipTrigger>
                                    <TooltipContent side="right">{it.label}</TooltipContent>
                                </Tooltip>
                            ) : (
                                <Fragment key={it.to}>{link}</Fragment>
                            );
                        })}
                        </div>
                        ))}
                    </nav>
                </TooltipProvider>

                <div className={`p-4 border-t border-white/10 ${collapsed ? "md:px-2" : ""}`}>
                    {/* Rail diciutkan: baris teks (nama/email/role) diganti avatar inisial +
                        tooltip, supaya info akun "tetap bisa diakses" tanpa memaksa lebar rail
                        melebar lagi. Logout tetap Button biasa di bawah (fungsi tidak berubah). */}
                    <div className={`flex items-center justify-between gap-2 mb-1 ${collapsed ? "md:hidden" : ""}`}>
                        <div className="text-xs text-white">Login sebagai</div>
                        <ThemeToggle />
                    </div>
                    <div className={`text-sm font-semibold truncate ${collapsed ? "md:hidden" : ""}`}>{user?.nama}</div>
                    <div className={`text-[11px] text-white truncate ${collapsed ? "md:hidden" : ""}`}>{user?.email}</div>
                    {/* Badge sebagai chip warna SOLID (bukan teks berwarna di atas teal) —
                        lime/turq-medium sbg teks langsung di atas teal cuma ~2-3:1 (gagal AA). */}
                    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${collapsed ? "md:hidden" : ""} ${
                        role === "super_admin" ? "bg-[#CDDC29] text-[#0F2E30]"
                        : role === "admin_ufp" ? "bg-[#60C0D0] text-[#0F2E30]"
                        : "bg-white text-[#267B84]"
                    }`}>
                        {ROLE_LABEL[role] || "Admin"}
                    </div>

                    {collapsed && (
                        <TooltipProvider delayDuration={200}>
                            {/* Avatar 36px (w-9/h-9) -- disamakan dgn skala default tombol icon
                                shadcn (size="icon" = h-9 w-9) yg dipakai Logout/ThemeToggle "large"
                                di bawah, supaya semua kontrol di rail collapsed terasa satu skala. */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className="hidden md:flex mx-auto mb-3 w-9 h-9 rounded-full bg-white/15 items-center justify-center text-sm font-bold text-white cursor-default"
                                        data-testid="sidebar-user-collapsed"
                                    >
                                        {(user?.nama || "?").trim().charAt(0).toUpperCase()}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    <div className="font-semibold">{user?.nama}</div>
                                    <div className="text-[11px] opacity-80">{user?.email}</div>
                                    <div className="text-[11px] opacity-80">{ROLE_LABEL[role] || "Admin"}</div>
                                </TooltipContent>
                            </Tooltip>
                            <div className="hidden md:flex justify-center mb-3">
                                <ThemeToggle large />
                            </div>
                        </TooltipProvider>
                    )}

                    <Button
                        data-testid="logout-button"
                        variant="ghost"
                        className={`w-full mt-3 text-white hover:bg-black/10 ${collapsed ? "md:justify-center md:px-0" : "justify-start"}`}
                        onClick={async () => { await logout(); navigate("/login"); }}
                    >
                        {/* Button shadcn otomatis memaksa svg turunannya ke 16px ([&_svg]:size-4,
                            spesifisitasnya menang lawan className biasa di icon) -- md:!w-5 md:!h-5
                            (prefix important) diperlukan supaya benar2 jadi 20px saat collapsed,
                            konsisten dgn ikon menu navigasi & avatar di atas. */}
                        <LogOut className={collapsed ? "md:mr-0 md:!w-5 md:!h-5" : "mr-2"} />
                        <span className={collapsed ? "md:hidden" : ""}>Logout</span>
                    </Button>
                </div>
            </aside>

            {/* Satu-satunya area yg scroll: overflow-y-auto di sini, terpisah dari sidebar. */}
            <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden overflow-y-auto">
                {/* Header mobile: hamburger selalu terlihat, sidebar desktop tetap seperti semula (md:hidden) */}
                <header className="md:hidden sticky top-0 z-20 flex items-center gap-3 h-14 px-4 bg-[#267B84] text-white shrink-0">
                    <button
                        type="button"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Buka menu"
                        data-testid="sidebar-open-button"
                        className="p-1.5 -ml-1.5 rounded-md hover:bg-black/10"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <img src={logo} alt={`AURA PPDS — ${TAGLINE}`} className="h-7 w-auto shrink-0" />
                </header>

                <main className="flex-1 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
